import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CalendarClock, Eye, FileText, Users } from 'lucide-react';
import { toast } from 'sonner';
import { DEFAULT_EMAIL_QUOTA } from '@/features/channel-quota/model/ledger';
import { useChannelQuotas } from '@/features/channel-quota/model/use-channel-quotas';
import { PageHeader } from '@/shared/components/page-header';
import { EmptyState } from '@/shared/components/empty-state';
import { Wizard, type WizardStep } from '@/shared/components/wizard';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { cn } from '@/shared/lib/cn';
import { usePassbook } from '@/shared/lib/passbook';
import { BackToCampaigns } from './campaign-actions';
import {
  applyMerge,
  AudienceBreakdown,
  CHANNEL_COPY,
  ChannelErrorState,
  ChannelLoading,
  ChoiceCard,
  ConsumptionDialog,
  formatCount,
  insertToken,
  marketingErrorToast,
  MERGE_FIELDS,
  reviewConsumption,
  ReviewSummaryCard,
  SendSchedule,
  snapshotFromSegment,
  toMarketingError,
  type AudienceSegment,
  type SendMode,
} from '@/features/marketing-shared';
import { EmailPreview } from './email-preview';
import { useEmailMarketing } from '../model/context';
import { EDITOR_META } from '../model/template';
import { useEmailMarketingPaths } from '../model/paths';
import { useEmailAudiences } from '../model/use-email-audiences';
import type { EmailBlock, EmailCampaign, EmailTemplate, SendingIdentity } from '../model/types';

const MAX_TAGS = 5;
const MAX_TAG_LENGTH = 191;
const MAX_SUBJECT = 998;

type ActiveField = 'subject' | 'preview' | `block:${string}`;

function localToIso(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** `YYYY-MM-DDTHH:mm` no fuso do navegador, para o campo de agendamento. */
function isoToLocal(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Amanhã às 10h: um horário futuro razoável para começar a editar. */
function defaultScheduleLocal(now = new Date()): string {
  const next = new Date(now);
  next.setDate(next.getDate() + 1);
  next.setHours(10, 0, 0, 0);
  return isoToLocal(next.toISOString());
}

function wizardHeader(existing: boolean) {
  return existing ? 'Editar campanha' : 'Nova campanha';
}

export function EmailCampaignWizardPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const paths = useEmailMarketingPaths();
  const { campaigns, campaignsState, templates, templatesState, identities, identitiesState } = useEmailMarketing();
  const audiencesQuery = useEmailAudiences();
  const existing = campaignId ? campaigns.find((item) => item.id === campaignId) ?? null : null;

  const states = [campaignsState, templatesState, identitiesState, audiencesQuery.state];
  const failed = states.find((state) => state.phase === 'error');
  const loading = states.some((state) => state.phase === 'loading');

  if (failed) {
    return (
      <>
        <PageHeader title={wizardHeader(Boolean(campaignId))} leading={<BackToCampaigns />} />
        <ChannelErrorState
          title="Não foi possível abrir a criação"
          error={failed.error}
          onRetry={() => states.forEach((state) => state.refetch())}
        />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <PageHeader
          title={wizardHeader(Boolean(campaignId))}
          description="Público, conteúdo e revisão."
          leading={<BackToCampaigns />}
        />
        <ChannelLoading label="Carregando a criação da campanha" />
      </>
    );
  }

  if (campaignId && !existing) {
    return (
      <>
        <PageHeader title="Editar campanha" leading={<BackToCampaigns />} />
        <Card>
          <EmptyState
            icon={FileText}
            title="Campanha não encontrada"
            description="Esse rascunho não existe nesta loja ou já foi excluído."
            action={
              <Button type="button" onClick={() => navigate(paths.list)}>
                Voltar às campanhas
              </Button>
            }
          />
        </Card>
      </>
    );
  }

  if (existing && existing.status !== 'draft') {
    return (
      <>
        <PageHeader title="Editar campanha" leading={<BackToCampaigns />} />
        <Card>
          <EmptyState
            icon={FileText}
            title="Só rascunho abre o editor"
            description="Os outros estados mostram o detalhe. Duplicar gera um rascunho novo."
            action={
              <Button type="button" onClick={() => navigate(paths.detail(existing.id))}>
                Ver campanha
              </Button>
            }
          />
        </Card>
      </>
    );
  }

  return (
    <WizardForm
      key={campaignId ?? 'nova'}
      existing={existing}
      templates={templates}
      identities={identities}
      audiences={audiencesQuery.audiences}
    />
  );
}

function WizardForm({
  existing,
  templates,
  identities,
  audiences,
}: {
  existing: EmailCampaign | null;
  templates: EmailTemplate[];
  identities: SendingIdentity[];
  audiences: AudienceSegment[];
}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { idPassbook, passbook } = usePassbook();
  const { quotas } = useChannelQuotas(idPassbook);
  const paths = useEmailMarketingPaths();
  const { approveCampaign } = useEmailMarketing();
  const requestedTemplateId = searchParams.get('template') ?? searchParams.get('modelo');
  const storeName = passbook.organizationName || 'Sua loja';
  const verifiedSenders = identities.filter((item) => item.status === 'verified');
  const defaultSender =
    (existing && identities.find((item) => item.fromEmail === existing.fromEmail)) ?? verifiedSenders[0] ?? null;
  const initialTemplate =
    templates.find((item) => item.id === (existing?.templateId ?? requestedTemplateId ?? '')) ?? templates[0] ?? null;
  const defaultAudience =
    (existing && audiences.find((item) => item.id === existing.audience.audienceId)) ??
    audiences.find((item) => item.eligible > 0) ??
    audiences[0] ??
    null;

  const [step, setStep] = useState(0);
  const [name, setName] = useState(existing?.name ?? '');
  const [fromName, setFromName] = useState(existing?.fromName || defaultSender?.fromName || storeName);
  const [fromEmail, setFromEmail] = useState(existing?.fromEmail || defaultSender?.fromEmail || '');
  const [tags, setTags] = useState<string[]>(existing?.tags ?? initialTemplate?.tags.slice(0, MAX_TAGS) ?? []);
  const [tagDraft, setTagDraft] = useState('');
  const [audienceId, setAudienceId] = useState(defaultAudience?.id ?? '');
  const [templateId, setTemplateId] = useState(initialTemplate?.id ?? '');
  const [subject, setSubject] = useState(existing?.subject || initialTemplate?.subject || '');
  const [previewText, setPreviewText] = useState(existing?.previewText || initialTemplate?.previewText || '');
  const [blocks, setBlocks] = useState<EmailBlock[]>(
    (existing?.blocks.length ? existing.blocks : initialTemplate?.blocks ?? []).map((block) => ({ ...block })),
  );
  const [sendMode, setSendMode] = useState<SendMode>(existing?.sendMode === 'now' ? 'now' : 'later');
  const [scheduledAt, setScheduledAt] = useState(isoToLocal(existing?.scheduledAt ?? null) || defaultScheduleLocal());
  const [activeField, setActiveField] = useState<ActiveField>('subject');
  const [partialSend, setPartialSend] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [serverBlocks, setServerBlocks] = useState<string[]>([]);
  const templateQueryRef = useRef(requestedTemplateId);

  useEffect(() => {
    if (templateQueryRef.current === requestedTemplateId) return;
    templateQueryRef.current = requestedTemplateId;
    if (!requestedTemplateId || existing) return;
    const next = templates.find((item) => item.id === requestedTemplateId);
    if (!next) return;
    setTemplateId(next.id);
    setSubject(next.subject);
    setPreviewText(next.previewText);
    setTags(next.tags.slice(0, MAX_TAGS));
    setBlocks(next.blocks.map((block) => ({ ...block })));
  }, [requestedTemplateId, existing, templates]);

  const audience = audiences.find((item) => item.id === audienceId) ?? null;
  const template = templates.find((item) => item.id === templateId) ?? null;
  const capacity = quotas?.email ?? DEFAULT_EMAIL_QUOTA;
  const review = reviewConsumption(audience?.eligible ?? 0, capacity, { unit: CHANNEL_COPY.email.unit });

  const applyTemplate = (next: EmailTemplate) => {
    setTemplateId(next.id);
    setSubject(next.subject);
    setPreviewText(next.previewText);
    setTags(next.tags.slice(0, MAX_TAGS));
    setBlocks(next.blocks.map((block) => ({ ...block })));
    toast.message('Template aplicado.', { description: 'Assunto, prévia, tags e textos continuam editáveis.' });
  };

  const addToken = (token: string) => {
    if (activeField === 'preview') {
      setPreviewText((current) => insertToken(current, token));
      return;
    }
    if (activeField.startsWith('block:')) {
      const id = activeField.slice('block:'.length);
      setBlocks((current) => current.map((block) => (block.id === id ? { ...block, text: insertToken(block.text, token) } : block)));
      return;
    }
    setSubject((current) => insertToken(current, token));
  };

  const approve = async () => {
    const sendingPartial = partialSend && review.canSendPartial;
    if (!audience || !template || (!review.canApprove && !sendingPartial)) return;
    const when = sendMode === 'later' ? localToIso(scheduledAt) : null;
    if (sendMode === 'later' && !when) {
      toast.error('Informe data e hora para agendar.');
      return;
    }
    setSubmitting(true);
    setServerBlocks([]);
    try {
      const result = await approveCampaign(
        {
          id: existing?.status === 'draft' ? existing.id : undefined,
          name,
          fromName,
          fromEmail,
          subject,
          previewText,
          templateId: template.id,
          templateName: template.name,
          tags,
          blocks,
          audience: snapshotFromSegment(audience),
          scheduledAt: when,
          sendMode,
        },
        { allowPartial: sendingPartial },
      );
      const people = result.campaign.audience.eligible;
      toast.success(result.leftOut > 0 ? 'Campanha aprovada com envio parcial.' : 'Campanha aprovada e agendada.', {
        description:
          result.leftOut > 0
            ? `${formatCount(people)} contatos entram. ${formatCount(result.leftOut)} ficam de fora. Os slots foram reservados no saldo da loja.`
            : `${formatCount(people)} e-mails reservados no saldo da loja. O envio sai pela agenda do FixeCRM.`,
      });
      navigate(paths.detail(result.campaign.id));
    } catch (error) {
      const mapped = toMarketingError(error, 'Não foi possível aprovar a campanha.');
      setServerBlocks(mapped.blocks.length ? mapped.blocks : [mapped.message]);
      const { message, description } = marketingErrorToast(error, 'Não foi possível aprovar a campanha.');
      toast.error(message, { description });
      setConfirmOpen(false);
      setStep(2);
    } finally {
      setSubmitting(false);
    }
  };

  // A cota ("Antes de aprovar") fica no dialog para não ocupar a revisão.
  const openApproval = () => {
    if (sendMode === 'later' && !localToIso(scheduledAt)) {
      toast.error('Informe data e hora para agendar.');
      return;
    }
    setConfirmOpen(true);
  };

  const steps: WizardStep[] = [
    {
      id: 'publico',
      title: 'Público',
      icon: Users,
      content: (
        <AudienceStep
          name={name}
          onName={setName}
          audiences={audiences}
          audienceId={audienceId}
          onAudience={setAudienceId}
          audience={audience}
          newSegmentationPath={`/loja/${idPassbook}/segmentacoes/nova?canal=email`}
        />
      ),
    },
    {
      id: 'conteudo',
      title: 'Conteúdo',
      icon: FileText,
      content: (
        <ContentStep
          templates={templates}
          templateId={templateId}
          onTemplate={applyTemplate}
          subject={subject}
          onSubject={setSubject}
          previewText={previewText}
          onPreviewText={setPreviewText}
          blocks={blocks}
          onBlock={(id, text) => setBlocks((current) => current.map((block) => (block.id === id ? { ...block, text } : block)))}
          onFocusField={setActiveField}
          onInsert={addToken}
          storeName={storeName}
          identities={identities}
          fromName={fromName}
          onFromName={setFromName}
          fromEmail={fromEmail}
          onFromEmail={(email) => {
            setFromEmail(email);
            const sender = identities.find((item) => item.fromEmail === email);
            if (sender?.fromName) setFromName(sender.fromName);
          }}
          tags={tags}
          onTags={setTags}
          tagDraft={tagDraft}
          onTagDraft={setTagDraft}
          templatesPath={paths.templates}
        />
      ),
    },
    {
      id: 'revisao',
      title: 'Revisão',
      icon: CalendarClock,
      content: (
        <ReviewStep
          name={name}
          audience={audience}
          templateName={template?.name ?? 'Sem template'}
          fromName={fromName}
          fromEmail={fromEmail}
          tags={tags}
          personalization={[...new Set(`${subject} ${previewText} ${blocks.map((block) => block.text).join(' ')}`.match(/\{\{[a-z_]+\}\}/g) ?? [])]}
          subject={applyMerge(subject, storeName)}
          sendMode={sendMode}
          onSendMode={setSendMode}
          scheduledAt={scheduledAt}
          onScheduledAt={setScheduledAt}
          serverBlocks={serverBlocks}
        />
      ),
    },
  ];

  const nextDisabled = step === 0 ? !name.trim() || !audience || audience.eligible === 0 : !subject.trim() || !template || !fromEmail;
  const sendingPartial = partialSend && review.canSendPartial;
  // Sem cota o botão continua abrindo o dialog: é lá que aparecem o motivo, o envio parcial e a recarga.
  const finishDisabled = !name.trim() || !subject.trim() || !template || !fromEmail || (sendMode === 'later' && !scheduledAt);

  return (
    <div className="min-w-0">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <PageHeader
          className="mb-0 min-w-0 max-w-full shrink-0"
          title={wizardHeader(Boolean(existing))}
          description="Público, conteúdo e revisão. A aprovação reserva o saldo e agenda o envio."
          leading={<BackToCampaigns />}
        />
        {audience ? (
          <AudienceBreakdown
            className="max-w-full justify-end"
            audience={snapshotFromSegment(audience)}
            tone="kpi"
          />
        ) : null}
      </div>
      <Wizard
        steps={steps}
        stepIndex={step}
        onStepIndexChange={setStep}
        onCancel={() => navigate(paths.list)}
        onFinish={openApproval}
        finishLabel="Aprovar agendamento"
        finishLoadingLabel="Aprovando…"
        isSubmitting={submitting}
        finishDisabled={finishDisabled}
        nextDisabled={nextDisabled}
        mobileOptimized
        clickableStepper
      />
      <ConsumptionDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={approve}
        confirming={submitting}
        confirmDisabled={!review.canApprove && !sendingPartial}
        eligible={audience?.eligible ?? 0}
        capacity={capacity}
        channel="email"
        passbookId={idPassbook}
        campaignName={name}
        partialAccepted={partialSend}
        onPartialAccepted={setPartialSend}
      />
    </div>
  );
}

function AudienceStep({
  name,
  onName,
  audiences,
  audienceId,
  onAudience,
  audience,
  newSegmentationPath,
}: {
  name: string;
  onName: (value: string) => void;
  audiences: AudienceSegment[];
  audienceId: string;
  onAudience: (value: string) => void;
  audience: AudienceSegment | null;
  newSegmentationPath: string;
}) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="nome-campanha">Nome da campanha</Label>
        <Input
          id="nome-campanha"
          className="mt-1.5"
          value={name}
          onChange={(event) => onName(event.target.value)}
          placeholder="Ex.: Ofertas de novembro"
          autoComplete="off"
        />
        {!name.trim() ? (
          <p className="mt-1 text-sm text-destructive" role="status">
            Dê um nome para continuar.
          </p>
        ) : null}
      </div>
      <fieldset className="space-y-3">
        <legend className="text-sm font-medium">Destinatários</legend>
        <p className="text-sm text-muted-foreground">
          Públicos são as segmentações de e-mail desta loja e as listas enviadas por arquivo. A contagem separa quem
          pode receber de quem está sem consentimento, descadastrado ou com e-mail inválido.
        </p>
        {audiences.length === 0 ? (
          <Card className="p-4 text-sm">
            <p className="font-medium">Nenhum público disponível</p>
            <p className="mt-1 text-muted-foreground">
              Crie uma segmentação com método de disparo E-mail. Ela aparece aqui depois de salva.
            </p>
            <Button type="button" variant="secondary" size="sm" className="mt-3" asChild>
              <Link to={newSegmentationPath}>Criar segmentação de e-mail</Link>
            </Button>
          </Card>
        ) : (
          <RadioGroup value={audienceId} onValueChange={onAudience} aria-label="Público da campanha" className="gap-3">
            {audiences.map((item) => {
              const selected = item.id === audienceId;
              return (
                <ChoiceCard
                  key={item.id}
                  selected={selected}
                  onSelect={() => onAudience(item.id)}
                  control={<RadioGroupItem value={item.id} id={`publico-${item.id}`} aria-labelledby={`publico-nome-${item.id}`} className="mt-0.5" />}
                >
                  <p id={`publico-nome-${item.id}`} className="text-base font-medium">
                    {item.name}
                  </p>
                  {item.description ? <p className="mt-1 text-sm text-muted-foreground">{item.description}</p> : null}
                  <p className="mt-2 text-sm">
                    <span className={cn('font-medium tabular-nums', item.eligible === 0 && 'text-destructive')}>
                      {formatCount(item.eligible)} elegíveis
                    </span>
                    <span className="text-muted-foreground">
                      {' '}
                      · {formatCount(item.excludedNoConsent)} sem consentimento · {formatCount(item.excludedUnsubscribed)}{' '}
                      descadastrados · {formatCount(item.excludedInvalid)} inválidos
                    </span>
                  </p>
                </ChoiceCard>
              );
            })}
          </RadioGroup>
        )}
      </fieldset>
      {audience && audience.eligible === 0 ? (
        <p className="rounded-xl border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive" role="status">
          Ninguém pode receber esta campanha. Sem consentimento, descadastrados e inválidos cobrem a lista inteira.
        </p>
      ) : null}
    </div>
  );
}

function ContentStep({
  templates,
  templateId,
  onTemplate,
  subject,
  onSubject,
  previewText,
  onPreviewText,
  blocks,
  onBlock,
  onFocusField,
  onInsert,
  storeName,
  identities,
  fromName,
  onFromName,
  fromEmail,
  onFromEmail,
  tags,
  onTags,
  tagDraft,
  onTagDraft,
  templatesPath,
}: {
  templates: EmailTemplate[];
  templateId: string;
  onTemplate: (template: EmailTemplate) => void;
  subject: string;
  onSubject: (value: string) => void;
  previewText: string;
  onPreviewText: (value: string) => void;
  blocks: EmailBlock[];
  onBlock: (id: string, text: string) => void;
  onFocusField: (field: ActiveField) => void;
  onInsert: (token: string) => void;
  storeName: string;
  identities: SendingIdentity[];
  fromName: string;
  onFromName: (value: string) => void;
  fromEmail: string;
  onFromEmail: (value: string) => void;
  tags: string[];
  onTags: (value: string[]) => void;
  tagDraft: string;
  onTagDraft: (value: string) => void;
  templatesPath: string;
}) {
  const addTag = () => {
    const next = tagDraft.trim().slice(0, MAX_TAG_LENGTH);
    if (!next || tags.includes(next) || tags.length >= MAX_TAGS) return;
    onTags([...tags, next]);
    onTagDraft('');
  };
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  const previewTemplate = templates.find((item) => item.id === previewTemplateId) ?? null;
  const previewIsCurrent = previewTemplate?.id === templateId;
  const hasVerified = identities.some((item) => item.status === 'verified');

  return (
    <div className="min-w-0 space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="min-w-0">
            <Label htmlFor="remetente-nome">Nome do remetente</Label>
            <Input id="remetente-nome" className="mt-1.5" value={fromName} onChange={(event) => onFromName(event.target.value)} autoComplete="off" />
          </div>
          <div className="min-w-0">
            <Label htmlFor="remetente-email">E-mail do remetente</Label>
            <Select value={fromEmail} onValueChange={onFromEmail} disabled={identities.length === 0}>
              <SelectTrigger id="remetente-email" className="mt-1.5">
                <SelectValue placeholder={identities.length === 0 ? 'Nenhum remetente liberado' : 'Domínio verificado'} />
              </SelectTrigger>
              <SelectContent>
                {identities.map((item) => (
                  <SelectItem key={item.id} value={item.fromEmail} disabled={item.status !== 'verified'}>
                    {item.fromEmail}
                    {item.status === 'verified' ? '' : ' · não verificado'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className={cn('text-xs', hasVerified ? 'text-muted-foreground' : 'text-destructive')}>
          {hasVerified
            ? 'O envio pede um remetente de domínio verificado, liberado para esta loja.'
            : 'Nenhum remetente verificado para esta loja. Peça ao time da Fixe para liberar o domínio em Configuração › Envio; sem isso a aprovação é recusada.'}
        </p>
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium">Template</legend>
          <p className="text-sm text-muted-foreground">Trocar o template substitui assunto, prévia e textos.</p>
          {templates.length === 0 ? (
            <Card className="p-4 text-sm">
              <p className="font-medium">Nenhum modelo nesta loja</p>
              <p className="mt-1 text-muted-foreground">Crie um modelo no editor antes de montar a campanha.</p>
              <Button type="button" variant="secondary" size="sm" className="mt-3" asChild>
                <Link to={templatesPath}>Ir para modelos</Link>
              </Button>
            </Card>
          ) : (
            <RadioGroup value={templateId} onValueChange={(id) => {
              const next = templates.find((item) => item.id === id);
              if (next) onTemplate(next);
            }} aria-label="Template da campanha" className="gap-2">
              {templates.map((item) => (
                <ChoiceCard
                  key={item.id}
                  selected={item.id === templateId}
                  onSelect={() => onTemplate(item)}
                  control={<RadioGroupItem value={item.id} id={`template-${item.id}`} aria-labelledby={`template-nome-${item.id}`} className="mt-0.5" />}
                  actions={
                    <Button type="button" variant="secondary" size="sm" onClick={() => setPreviewTemplateId(item.id)}>
                      <Eye /> Ver prévia
                    </Button>
                  }
                >
                  <p id={`template-nome-${item.id}`} className="font-medium">
                    {item.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {[item.category, EDITOR_META[item.editor].label, item.description].filter(Boolean).join(' · ')}
                  </p>
                </ChoiceCard>
              ))}
            </RadioGroup>
          )}
        </fieldset>
        <div>
          <Label htmlFor="assunto">Assunto</Label>
          <Input
            id="assunto"
            className="mt-1.5"
            value={subject}
            maxLength={MAX_SUBJECT}
            onChange={(event) => onSubject(event.target.value)}
            onFocus={() => onFocusField('subject')}
            autoComplete="off"
          />
          {!subject.trim() ? (
            <p className="mt-1 text-sm text-destructive" role="status">
              Escreva o assunto para continuar.
            </p>
          ) : null}
        </div>
        <div>
          <Label htmlFor="preview-text">Texto de pré-visualização</Label>
          <Input
            id="preview-text"
            className="mt-1.5"
            value={previewText}
            onChange={(event) => onPreviewText(event.target.value)}
            onFocus={() => onFocusField('preview')}
            autoComplete="off"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Aparece ao lado do assunto na caixa de entrada.
          </p>
        </div>
        <div>
          <Label htmlFor="tag-campanha">Tags</Label>
          <div className="mt-1.5 flex flex-col gap-2 sm:flex-row">
            <Input
              id="tag-campanha"
              className="min-w-0"
              value={tagDraft}
              maxLength={MAX_TAG_LENGTH}
              disabled={tags.length >= MAX_TAGS}
              placeholder={tags.length >= MAX_TAGS ? 'Limite de 5 tags' : 'Digite e pressione Enter'}
              onChange={(event) => onTagDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addTag();
                }
              }}
              autoComplete="off"
            />
            <Button type="button" variant="secondary" onClick={addTag} disabled={tags.length >= MAX_TAGS || !tagDraft.trim()}>
              Adicionar
            </Button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {tags.map((tag) => (
              <Button key={tag} type="button" variant="secondary" size="sm" onClick={() => onTags(tags.filter((item) => item !== tag))}>
                {tag} ×
              </Button>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{tags.length}/{MAX_TAGS}. No envio, estas tags substituem as do template.</p>
        </div>
        <div>
          <p className="text-sm font-medium">Personalização</p>
          <p className="mt-1 text-xs text-muted-foreground">Insere {'{{variavel}}'} no campo em foco. Na prévia, vira o exemplo da Ana.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {MERGE_FIELDS.map((field) => (
              <Button key={field.token} type="button" variant="secondary" size="sm" onClick={() => onInsert(field.token)}>
                {field.label}
              </Button>
            ))}
          </div>
        </div>
        {blocks.map((block) => (
          <div key={block.id}>
            <Label htmlFor={`bloco-${block.id}`}>{block.label}</Label>
            <Textarea
              id={`bloco-${block.id}`}
              className="mt-1.5"
              rows={block.id === 'texto' ? 5 : 2}
              value={block.text}
              onChange={(event) => onBlock(block.id, event.target.value)}
              onFocus={() => onFocusField(`block:${block.id}`)}
            />
          </div>
        ))}
      <Dialog open={previewTemplate != null} onOpenChange={(open) => { if (!open) setPreviewTemplateId(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{previewTemplate?.name ?? 'Prévia'}</DialogTitle>
          </DialogHeader>
          {previewTemplate ? (
            <EmailPreview
              subject={previewIsCurrent ? subject : previewTemplate.subject}
              previewText={previewIsCurrent ? previewText : previewTemplate.previewText}
              blocks={previewIsCurrent ? blocks : previewTemplate.blocks}
              storeName={storeName}
              fromName={fromName}
              fromEmail={fromEmail}
              editor={previewTemplate.editor}
              html={previewTemplate.html}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReviewStep({
  name,
  audience,
  templateName,
  fromName,
  fromEmail,
  tags,
  personalization,
  subject,
  sendMode,
  onSendMode,
  scheduledAt,
  onScheduledAt,
  serverBlocks,
}: {
  name: string;
  audience: AudienceSegment | null;
  templateName: string;
  fromName: string;
  fromEmail: string;
  tags: string[];
  personalization: string[];
  subject: string;
  sendMode: SendMode;
  onSendMode: (value: SendMode) => void;
  scheduledAt: string;
  onScheduledAt: (value: string) => void;
  serverBlocks: string[];
}) {
  return (
    <div className="space-y-4">
      {serverBlocks.length > 0 ? (
        <section className="rounded-2xl border border-destructive/40 bg-destructive/5 p-4 sm:p-5" role="alert">
          <h2 className="text-base font-semibold">O servidor recusou a aprovação</h2>
          <ul className="mt-2 space-y-1">
            {serverBlocks.map((block) => (
              <li key={block} className="text-sm font-medium text-destructive">
                {block}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-sm text-muted-foreground">Corrija o que está listado e aprove de novo. Nada foi reservado.</p>
        </section>
      ) : null}
      <ReviewSummaryCard
        rows={[
          { label: 'Campanha', value: name },
          { label: 'Remetente', value: `${fromName || fromEmail} <${fromEmail}>`, valueClassName: 'break-all' },
          { label: 'Destinatários', value: audience ? `${audience.name} · ${formatCount(audience.eligible)}` : '—' },
          { label: 'Assunto', value: subject, valueClassName: 'max-w-full sm:max-w-[18rem]' },
          { label: 'Template', value: templateName },
          { label: 'Tags', value: tags.length ? tags.join(', ') : 'Nenhuma' },
          { label: 'Personalização', value: personalization.length ? personalization.join(' ') : 'Nenhuma' },
        ]}
      />
      <SendSchedule
        idPrefix="email-envio"
        sendMode={sendMode}
        onSendMode={onSendMode}
        scheduledAt={scheduledAt}
        onScheduledAt={onScheduledAt}
      />
    </div>
  );
}
